import classNames from 'classnames';
import { format, isValid, parse } from 'date-fns';

import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { IMaskInput } from 'react-imask';

import { CommonApi } from '../../../api';
import type { Medicine } from '../../../model';
import { DatePickerModal, Modal } from '../../molecules';
import {
  ChevronDownIcon,
  DurationField,
  ErrorBlock,
  FieldLabel,
  IntervalField,
  RadioOption,
  hourFromTimeValue,
} from './medicine-fields';
import type { TimeWithCount } from './medicine-fields';
import {
  DAYTIME_OPTIONS,
  FREQUENCY_MODE_OPTIONS,
  formatMedicineSubtitle,
  resolveDaytimeId,
  resolveFrequency,
} from './order-medicine';
import type { DaytimeId, FrequencyMode, OrderMedicine } from './order-medicine';
import { SelectMedicineModal } from './select-medicine-modal';

const CalendarIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M19 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1ZM16 2v4M8 2v4M4 9h16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = ({ className = '' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/** Часть суток по часу приёма — для восстановления выбора при редактировании. */
const daytimeFromHour = (hour: number): DaytimeId => {
  const exact = DAYTIME_OPTIONS.find((option) => option.hour === hour);
  if (exact) return exact.id;
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

type Props = {
  isOpen: boolean;
  /** Препарат, который редактируем; пусто — добавляем новый. */
  initial?: OrderMedicine;
  onClose: () => void;
  onSave: (medicine: OrderMedicine) => void;
};

/**
 * «Добавить лекарство»: препарат, частота, время суток и курс — по одному
 * решению на экран. Несколько приёмов в день собираются повторным добавлением
 * того же препарата с другим временем.
 */
export const AddMedicineModal = ({
  isOpen,
  initial,
  onClose,
  onSave,
}: Props) => {
  const { data: frequencyList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 200],
    queryFn: () => CommonApi.getReference(200),
  });
  const { data: timeList = [] } = useQuery({
    queryKey: [CommonApi.KEY, 'getReference', 202],
    queryFn: () => CommonApi.getReference(202),
  });

  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [medicine, setMedicine] = useState<Medicine | undefined>(
    initial?.medicine,
  );
  const [mode, setMode] = useState<FrequencyMode>(
    initial?.frequencyMode ?? 'daily',
  );
  const [timesPerInterval, setTimesPerInterval] = useState(
    initial?.timesPerInterval ?? 1,
  );
  const [intervalDays, setIntervalDays] = useState(initial?.intervalDays ?? 3);
  const [daytime, setDaytime] = useState<DaytimeId>(
    initial?.time?.[0]
      ? daytimeFromHour(hourFromTimeValue(initial.time[0].value))
      : 'morning',
  );
  const [start, setStart] = useState(() => {
    if (!initial?.start) return '';
    const parsed = parse(initial.start, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? format(parsed, 'dd.MM.yyyy') : '';
  });
  const [duration, setDuration] = useState(initial?.duration ?? 1);
  const [error, setError] = useState<string | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const dateFieldRef = useRef<HTMLDivElement>(null);

  const startDate = parse(start, 'dd.MM.yyyy', new Date());
  const isStartFilled = start.length === 10 && isValid(startDate);

  const handleSave = () => {
    if (!medicine) {
      setError('Выберите лекарство');
      return;
    }
    if (!start || !isValid(startDate)) {
      setError('Укажите дату начала приёма');
      return;
    }
    const frequency = resolveFrequency(frequencyList, mode, intervalDays);
    if (!frequency) {
      setError('Не удалось загрузить справочник частоты приёма');
      return;
    }

    const option = DAYTIME_OPTIONS.find((item) => item.id === daytime)!;
    const timeId = resolveDaytimeId(timeList, daytime) ?? option.hour;
    const slot: TimeWithCount = {
      timeId,
      // Час нужен интерфейсу: из него собирается «Утром в 8:00» в карточке.
      value: `${String(option.hour).padStart(2, '0')}:00`,
      count: 1,
    };

    onSave({
      totalCount: duration,
      count: 1,
      frequency,
      medicine,
      time: [slot],
      duration,
      start: format(startDate, 'yyyy-MM-dd'),
      intakeMethod: initial?.intakeMethod,
      frequencyMode: mode,
      timesPerInterval,
      intervalDays,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      showCloseButton={false}
      panelClassName="md:max-w-[470px]"
    >
      <div className="flex w-full flex-col gap-6 p-5 md:gap-8 md:p-8">
        <div className="flex w-full items-center gap-8">
          <h2 className="flex-1 text-xl font-medium leading-8 text-[#151515] md:text-2xl">
            {initial ? 'Лекарство' : 'Добавить лекарство'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-6 shrink-0 items-center justify-center text-[#151515] opacity-50"
            aria-label="Закрыть"
          >
            <CloseIcon className="size-6" />
          </button>
        </div>

        <div className="flex w-full flex-col gap-6">
          <button
            type="button"
            className="flex min-h-14 w-full items-center gap-3 rounded-xl border border-[#afc4d1] px-5 py-[18px] text-left transition hover:border-[#0788f1]"
            onClick={() => setIsCatalogOpen(true)}
          >
            <span className="flex min-w-0 flex-1 flex-col items-start justify-center gap-1">
              <span
                className={classNames(
                  'w-full truncate text-base font-normal leading-5 text-[#151515]',
                  !medicine && 'opacity-50',
                )}
              >
                {medicine ? medicine.name : 'Лекарство'}
              </span>
              {medicine && formatMedicineSubtitle(medicine) && (
                <span className="w-full truncate text-sm font-normal leading-4 text-[#151515] opacity-50">
                  {formatMedicineSubtitle(medicine)}
                </span>
              )}
            </span>
            <ChevronDownIcon className="size-6 shrink-0 text-[#151515]" />
          </button>
          <SelectMedicineModal
            isOpen={isCatalogOpen}
            onClose={() => setIsCatalogOpen(false)}
            onMedicineSelect={(selected) => {
              setMedicine(selected);
              setError(null);
              setIsCatalogOpen(false);
            }}
          />

          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-col gap-3">
              <FieldLabel size="base">Как часто принимать?</FieldLabel>
              <div className="flex flex-col gap-1">
                {FREQUENCY_MODE_OPTIONS.map((option) => (
                  <div key={option.id} className="py-1">
                    <RadioOption
                      checked={mode === option.id}
                      label={option.label}
                      onChange={() => setMode(option.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
            {mode === 'interval' && (
              <IntervalField
                timesPerInterval={timesPerInterval}
                intervalDays={intervalDays}
                onTimesChange={setTimesPerInterval}
                onDaysChange={setIntervalDays}
              />
            )}
          </div>

          <div className="flex w-full flex-col gap-3">
            <FieldLabel size="base">Во сколько?</FieldLabel>
            <div className="flex flex-col gap-1">
              {DAYTIME_OPTIONS.map((option) => (
                <div key={option.id} className="py-1">
                  <RadioOption
                    checked={daytime === option.id}
                    label={
                      daytime === option.id
                        ? `${option.label} в ${String(option.hour).padStart(
                            2,
                            '0',
                          )}:00`
                        : option.label
                    }
                    onChange={() => setDaytime(option.id)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex w-full flex-col gap-3">
            <FieldLabel size="base">Курс</FieldLabel>
            <div
              ref={dateFieldRef}
              className="relative flex min-h-14 w-full items-center gap-3 rounded-xl border border-[#afc4d1] px-5 py-[18px] transition focus-within:border-[#0788f1]"
            >
              <IMaskInput
                mask="00.00.0000"
                lazy
                overwrite
                inputMode="numeric"
                value={start}
                onAccept={(value: string) => {
                  setStart(value);
                  setError(null);
                }}
                placeholder="Дата начала"
                unmask={false}
                className="w-0 flex-1 bg-transparent text-base font-normal leading-5 text-[#151515] outline-none placeholder:text-[#151515]/50"
                aria-label="Дата начала"
              />
              <button
                type="button"
                onClick={() => setIsCalendarOpen(true)}
                className="relative flex size-6 shrink-0 items-center justify-center text-[#151515]"
                aria-label="Выбрать дату в календаре"
              >
                <CalendarIcon className="size-6" />
              </button>
            </div>
            <DatePickerModal
              isOpen={isCalendarOpen}
              onClose={() => setIsCalendarOpen(false)}
              value={isStartFilled ? startDate : undefined}
              anchorRef={dateFieldRef}
              onSelect={(date) => {
                setStart(format(date, 'dd.MM.yyyy'));
                setError(null);
              }}
            />
            <DurationField
              label="Сколько дней?"
              value={duration}
              onChange={setDuration}
            />
          </div>

          {error && <ErrorBlock message={error} />}
        </div>

        <div className="flex w-full items-start gap-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-[60px] flex-1 items-center justify-center rounded-xl bg-[#0788f1]/[0.08] p-5 text-base font-bold leading-5 text-[#0788f1]"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="flex h-[60px] flex-1 items-center justify-center rounded-xl bg-[#0788f1] p-5 text-base font-bold leading-5 text-white"
          >
            Сохранить
          </button>
        </div>
      </div>
    </Modal>
  );
};
